import { useEditNotificationReadStateMutation } from 'store/services/core/notification';
import { memo, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';

import { ReactNativeMessageType, type NotificationItemType } from 'types';
import { invokeRNMessage, isMobileApp } from 'utils/common';
import { useLogin, useStore } from 'contexts';
import { STORE_KEYS } from 'types/store';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { getNotificationLinkFromTarget } from '../utils/notificationLink';
import { ContentWrapper, DateText, Forwarded, NotificationContainer, ReadIndicator, SubjectSpan, UserHighlight } from './NotificationItem.styles';

interface Props extends NotificationItemType {
  onNotificationUpdateClick: () => void;
  closeMenu: () => void;
}

export const NotificationItem = memo(
  ({
    id,
    subject,
    bodyRichText,
    readState,
    requiresAction,
    createdAt,
    onNotificationUpdateClick,
    moduleId,
    agendaId,
    recordId,
    usersIds,
    closeMenu,
    isCreatedByFlow,
    isCreatedByHelios,
    hasDetail,
  }: Props) => {
    const [editNotificationReadState] = useEditNotificationReadStateMutation();
    const mobileApp = isMobileApp();
    const { getItem } = useStore();
    const navigate = useNavigate();
    const auth = getItem(STORE_KEYS.AUTH);
    const { user } = useLogin();
    const { t } = useTranslation();

    const originalUser = useMemo(
      () =>
        usersIds.find(
          (userId) => userId.notifiedUser === user?.user_id && userId.originalUser !== userId.notifiedUser && userId.originalUser !== null
        ),
      [user?.user_id, usersIds]
    );

    const handleNotificationClick = useCallback(async () => {
      if (requiresAction) return;
      await editNotificationReadState({
        userNotificationsIds: [id],
        state: 'Read',
      });
      onNotificationUpdateClick();
    }, [editNotificationReadState, id, onNotificationUpdateClick, requiresAction]);

    const downloadContent = useCallback(
      async (url: string) => {
        try {
          if (mobileApp) return invokeRNMessage(ReactNativeMessageType.DownloadNotificationFile, { url, token: auth?.jwtToken ?? '' });

          const response = await fetch(url, {
            headers: {
              Authorization: auth?.jwtToken ?? '',
            },
          });
          if (!response.ok) return toast.error(t('common.failedToDownload'));

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);

          const parsedUrl = new URL(url);
          const objectName = parsedUrl.searchParams.get('objectName');
          const fallbackFileName = 'export';
          const fileName = objectName?.replace(/\.[^/.]+$/, '') || fallbackFileName;
          const extensionBasedOnContentType = blob.type.split('/')[1];

          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = `${fileName}.${extensionBasedOnContentType}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
        } catch (_error) {
          toast.error(t('common.failedToDownload'));
        }
      },
      [auth?.jwtToken, mobileApp, t]
    );

    const sanitizedBodyRichText = useMemo(() => {
      if (!bodyRichText.includes('<a')) return bodyRichText;

      const doc = new DOMParser().parseFromString(bodyRichText, 'text/html');

      doc.querySelectorAll('a').forEach((link) => {
        const span = doc.createElement('span');
        span.textContent = link.textContent;
        span.style.textDecoration = 'underline';
        span.style.fontWeight = '600';
        span.style.cursor = 'pointer';
        span.setAttribute('data-url', link.getAttribute('href') || '');
        link.parentNode?.replaceChild(span, link);
      });

      return doc.body.innerHTML;
    }, [bodyRichText]);

    const handleSubjectClick = useCallback(async () => {
      closeMenu();

      const hasNavigationTarget = Boolean(moduleId && agendaId && recordId);

      if (hasNavigationTarget && readState === 'Unread' && !requiresAction) {
        await handleNotificationClick();
      }

      setTimeout(() => {
        if (!hasNavigationTarget) return;

        if (isCreatedByFlow) {
          navigate(`/user/modules/${moduleId}/Flow/${agendaId}/r/${recordId}/detail`);
          return;
        }
        if (isCreatedByHelios) {
          if (hasDetail === false) {
            navigate(`/user/modules/${moduleId}/Helios/${agendaId}`);
          } else {
            navigate(`/user/modules/${moduleId}/Helios/${agendaId}/r/${recordId}/detail`, { state: { navigateBack: true } });
          }
          return;
        }
        navigate(`/user/modules/${moduleId}/Flow/${agendaId}/r/${recordId}/detail`);
      }, 100);
    }, [
      moduleId,
      agendaId,
      recordId,
      navigate,
      closeMenu,
      isCreatedByFlow,
      isCreatedByHelios,
      hasDetail,
      readState,
      requiresAction,
      handleNotificationClick,
    ]);

    const handleCardClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (!mobileApp) return;
        if (getNotificationLinkFromTarget(event.target)) return;

        handleSubjectClick();
      },
      [mobileApp, handleSubjectClick]
    );

    const handleLinkClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        const url = getNotificationLinkFromTarget(event.target)?.getAttribute('data-url') ?? null;
        if (!url) return;

        event.preventDefault();
        event.stopPropagation();
        if (url.includes('/documentstorage/')) {
          downloadContent(url);
          return;
        }

        window.open(url, '_blank');
      },
      [downloadContent]
    );

    return (
      <NotificationContainer
        onClick={mobileApp ? handleCardClick : undefined}
        sx={{
          opacity: readState === 'Read' ? 0.75 : 1,
        }}>
        <ContentWrapper>
          <div>
            <SubjectSpan onClick={mobileApp ? undefined : handleSubjectClick}>{subject}</SubjectSpan>
          </div>
          <div
            onClick={handleLinkClick}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(sanitizedBodyRichText),
            }}
          />
          {originalUser && (
            <Forwarded>
              <Trans
                i18nKey="notification.forwarded"
                components={{
                  UserHighlight: <UserHighlight />,
                }}
                values={{ user: originalUser.originalUserName }}
              />
            </Forwarded>
          )}
          <DateText
            sx={{
              display: createdAt ? 'block' : 'none',
            }}>
            {createdAt && new Date(createdAt).toLocaleDateString()}
          </DateText>
        </ContentWrapper>
        <div>
          {readState === 'Unread' && (
            <ReadIndicator
              onClick={mobileApp ? undefined : handleNotificationClick}
              sx={{
                cursor: requiresAction ? 'not-allowed' : 'pointer',
              }}
            />
          )}
        </div>
      </NotificationContainer>
    );
  }
);
